"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useUser } from "@clerk/nextjs";
import { CommentFormData } from "@/lib/types";
import Image from "next/image";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import NameChangeModal from "./NameChangeModal";
import MembershipBadge from "@/components/MembershipBadge";
import BlockEditor from "./BlockEditor";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { uploadBlockEditorImages } from "@/lib/block-editor-image-upload";

interface CommentFormProps {
  onSubmit: (data: CommentFormData) => boolean | void | Promise<boolean | void>;
  onCancel?: () => void;
  initialData?: Partial<CommentFormData>;
  isReply?: boolean;
  isEdit?: boolean;
}

/**
 * 댓글 작성 폼 컴포넌트
 * 새 댓글, 답글, 수정 모드에서 사용됩니다.
 */
export default function CommentForm({
  onSubmit,
  onCancel,
  initialData = {},
  isReply = false,
  isEdit = false,
}: CommentFormProps) {
  const { isSignedIn, user } = useUser();
  const { passType: effectivePassType } = useEffectivePass();

  // Clerk 사용자 정보가 있으면 우선 사용, 없으면 initialData 사용
  const getInitialFormData = (): CommentFormData => {
    if (isSignedIn && user && !isEdit) {
      return {
        author: user.fullName || user.firstName || user.username || "익명",
        email: user.primaryEmailAddress?.emailAddress || "",
        content: initialData.content || "",
        clerkUserId: user.id,
        profileImageUrl: user.imageUrl,
        passType: effectivePassType,
      };
    }
    return {
      author: initialData.author || "",
      email: initialData.email || "",
      content: initialData.content || "",
      clerkUserId: initialData.clerkUserId || null,
      profileImageUrl: initialData.profileImageUrl || null,
      passType: initialData.passType || null,
    };
  };

  const [formData, setFormData] =
    useState<CommentFormData>(getInitialFormData());

  const [showNameModal, setShowNameModal] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const imageFileMapRef = useRef<Map<string, File>>(new Map());
  const editVersionRef = useRef(0);

  // 로그인 사용자의 정보가 변경되면 formData도 업데이트 — Clerk 외부 인증 상태 동기화
  useEffect(() => {
    if (isSignedIn && user && !isEdit) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormData((prev) => ({
        ...prev,
        author: user.fullName || user.firstName || user.username || "익명",
        email: user.primaryEmailAddress?.emailAddress || "",
        clerkUserId: user.id,
        profileImageUrl: user.imageUrl,
        passType: effectivePassType,
      }));
    }
  }, [isSignedIn, user, isEdit, effectivePassType]);

  // 폼 데이터 업데이트 핸들러 (이름/이메일 input용)
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    editVersionRef.current += 1;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // BlockEditor 콘텐츠 변경 핸들러
  const handleContentChange = (newContent: string) => {
    editVersionRef.current += 1;
    setFormData((prev) => ({ ...prev, content: newContent }));
  };

  const handleImageFilesChange = useCallback((nextMap: Map<string, File>) => {
    imageFileMapRef.current = nextMap;
  }, []);

  // 폼 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !formData.author.trim() ||
      !formData.email.trim() ||
      !formData.content.trim()
    ) {
      alert("모든 필드를 입력해주세요.");
      return;
    }

    setIsUploading(true);
    const editVersionAtSubmit = editVersionRef.current;
    const formDataAtSubmit = formData;
    const imageFileMapAtSubmit = new Map(imageFileMapRef.current);

    try {
      // BlockEditor 내 인라인 이미지의 blob URL을 실제 URL로 치환
      const processedContent = await uploadBlockEditorImages({
        content: formDataAtSubmit.content,
        imageFileMap: imageFileMapAtSubmit,
        uploadEndpoint: "/api/upload-comment-image",
      });
      if (editVersionRef.current !== editVersionAtSubmit) {
        alert("이미지 업로드 중 입력이 변경되었습니다. 다시 작성 버튼을 눌러주세요.");
        return;
      }

      // Clerk 사용자 정보 포함하여 제출
      const passType = isSignedIn && user
        ? effectivePassType
        : null;
      const submitData: CommentFormData = {
        ...formDataAtSubmit,
        content: processedContent,
        clerkUserId: isSignedIn && user ? user.id : null,
        profileImageUrl: isSignedIn && user ? user.imageUrl : null,
        passType,
        imageUrls: [],
      };

      const submitResult = await onSubmit(submitData);

      if (submitResult === false) return;

      if (!isEdit) {
        // 수정 모드가 아닐 때만 폼 초기화
        setFormData(getInitialFormData());
        imageFileMapRef.current = new Map();
      }
    } catch (error) {
      console.error("댓글 제출 오류:", error);
      alert("댓글 처리에 실패했습니다.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* 답글인 경우 구분선 표시 */}
      {isReply && (
        <div className="pl-1">
          <h4 className="text-sm font-medium text-[#333] dark:text-neutral-300 mb-2">
            답글
          </h4>
        </div>
      )}

      {/* 로그인 사용자 정보 표시 또는 입력 필드 */}
      {isSignedIn && user && !isEdit ? (
        <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-neutral-400">
          <Image
            src={profileImageError ? "/default_gamja.webp" : user.imageUrl}
            alt="프로필"
            width={32}
            height={32}
            className="w-8 h-8 rounded-full"
            onError={() => setProfileImageError(true)}
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-medium text-[#333] dark:text-neutral-200">
                {user.fullName || user.firstName || user.username || "익명"}
              </div>
              <MembershipBadge passType={effectivePassType} />
              <button
                type="button"
                onClick={() => setShowNameModal(true)}
                className="text-xs text-[#706E6E] dark:text-gray-400 hover:underline"
              >
                이름 바꾸기
              </button>
            </div>
            <div className="text-xs text-[#706E6E] dark:text-gray-400">
              {user.primaryEmailAddress?.emailAddress}
            </div>
          </div>
          <div className="text-xs text-green-600 dark:text-green-400 font-medium">
            로그인됨
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-4">
          {/* 글쓴이 입력 필드 */}
          <div className="flex-1">
            <input
              type="text"
              name="author"
              value={formData.author}
              onChange={handleChange}
              placeholder="이름"
              className="w-full px-3 py-2 border-[0.5px] border-gray-200 dark:border-neutral-400 rounded-md focus:outline-none focus:ring-2 focus:ring-[#706E6E] dark:bg-gray-700 dark:text-white text-base"
              required
              disabled={isSignedIn && user && !isEdit}
            />
          </div>

          {/* 이메일 입력 필드 (안내 툴팁 포함) */}
          <div className="flex-1 relative">
            <div className="flex items-center gap-2">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="이메일"
                className="w-full px-3 py-2 border-[0.5px] border-gray-200 dark:border-neutral-400 rounded-md focus:outline-none focus:ring-2 focus:ring-[#706E6E] dark:bg-gray-700 dark:text-white text-base"
                required
                disabled={isSignedIn && user && !isEdit}
              />
              {/* 이메일 안내 툴팁 */}
              <Tooltip tapToOpen>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="이메일 사용 안내"
                    className="flex min-h-7 min-w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    !
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  이메일은 비공개입니다. 답글 알림, 댓글 수정, 삭제 등 댓글
                  관리에 사용됩니다.
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      )}

      {/* 댓글 내용 입력 - BlockEditor (이미지 붙여넣기/삽입 포함) */}
      <BlockEditor
        value={formData.content}
        onChange={handleContentChange}
        placeholder="댓글을 입력해주세요."
        onImageFilesChange={handleImageFilesChange}
        maxImages={3}
      />

      {/* 버튼들 */}
      <div className="flex gap-2 justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-[#706E6E] border-[0.5px] border-gray-200 dark:border-neutral-400 rounded-md hover:bg-gray-50 dark:text-neutral-300 dark:hover:bg-gray-700 text-sm font-light"
          >
            취소
          </button>
        )}
        <button
          type="submit"
          disabled={isUploading}
          className="px-6 py-2 bg-[#706E6E] text-white rounded-md hover:bg-[#5A5858] transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? "업로드 중..." : "작성"}
        </button>
      </div>

      {/* 이름 변경 모달 */}
      <NameChangeModal
        isOpen={showNameModal}
        onClose={() => setShowNameModal(false)}
      />
    </form>
  );
}
